import './Call.sass';

import React, { FC, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useStopwatch } from 'react-timer-hook';
import { useDispatch, useSelector } from 'react-redux';
import SimplePeer from 'simple-peer';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import { Api } from '$api';
import { ModalWrapper, RoundButton } from '$uikit';
import { imActions, imCallSelector } from '$store/im';
import { CallStatus, CallType } from '$store/im/interface';
import { CallPush } from '$core/Call/CallPush/CallPush';
import { toastActions } from '$store/toast';
import { IcCam28, IcClose28, IcFullscreen28, IcMicOff28 } from '$assets';
import { classNames as cn, statReachGoal } from '$utils';
import { UnitName, UnitPhoto } from '$shared/components';
import { mainSelector } from '$store/main';
import { useDesktop, useTranslator } from '$hooks';

const CallComponent: FC = () => {
  const localVideo = useRef<HTMLVideoElement>(null);
  const remoteVideo = useRef<HTMLVideoElement>(null);
  const handle = useFullScreenHandle();
  const callStartTime = useRef(0);
  const [hasStream, setHasStream] = useState(false);
  const { start, hours, minutes, seconds } = useStopwatch({ autoStart: false });
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localVideoEnabled, setLocalVideoEnabled] = useState<boolean | null>(null);
  const [remoteAudioEnabled, setRemoteAudioEnabled] = useState(true);
  const [remoteVideoEnabled, setRemoteVideoEnabled] = useState(false);
  const peerRef = useRef<SimplePeer.Instance>();
  const stream = useRef<MediaStream>();
  const [displayUI, setDisplayUI] = useState(true);
  const { status, peerId, type, remoteDescription, iceServers } = useSelector(imCallSelector);
  const [remoteScreen, setRemoteScreen] = useState({ width: 0, height: 0 });
  const { users } = useSelector(mainSelector);
  const user = users[peerId];
  const initiator = type !== CallType.INCOMING;
  const dispatch = useDispatch();
  const isConnected = status === CallStatus.CONNECTED;
  const isDesktop = useDesktop();
  const t = useTranslator();

  const handleHangUp = useCallback(
    (e) => {
      e.stopPropagation();
      if (status === CallStatus.CONNECTED) {
        peerRef.current?.send('hangup');
        dispatch(imActions.hangup());
      } else {
        dispatch(imActions.callCancel(peerId));
      }
      statReachGoal('call_hangup', {
        peerId,
      });
    },
    [status, peerRef, peerId],
  );

  const handleToggleAudio = useCallback(
    (e) => {
      e.stopPropagation();
      stream.current
        ?.getAudioTracks()
        .forEach((track) => (track.enabled = !localAudioEnabled));
      peerRef.current?.send(!localAudioEnabled ? 'audioUnmute' : 'audioMute');
      setLocalAudioEnabled(!localAudioEnabled);
    },
    [localAudioEnabled],
  );

  const handleToggleVideo = useCallback(
    (e) => {
      e.stopPropagation();
      stream.current
        ?.getVideoTracks()
        .forEach((track) => (track.enabled = !localVideoEnabled));
      peerRef.current?.send(!localVideoEnabled ? 'videoUnmute' : 'videoMute');
      setLocalVideoEnabled(!localVideoEnabled);
    },
    [localVideoEnabled],
  );

  useEffect(() => {
    if (status === CallStatus.CONNECTED) {
      start();
      callStartTime.current = Date.now();
      statReachGoal('call_start', {
        peerId,
      });
    } else if (status === CallStatus.COMPLETED) {
      dispatch(toastActions.setToastFail(t('call_peer_completed_call')));
    } else if (status === CallStatus.CANCELED) {
      dispatch(toastActions.setToastFail(t('call_connection_close')));
      dispatch(imActions.hangup());
    }
  }, [status]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const hasVideoDevices =
        devices.filter((device) => device.kind === 'videoinput').length > 0;
      if (hasVideoDevices && localVideoEnabled === null) {
        setLocalVideoEnabled(false);
      }

      document.body.classList.add('call');

      navigator.mediaDevices
        .getUserMedia({
          video: hasVideoDevices,
          audio: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true,
          },
        })
        .then((s) => {
          setHasStream(true);
          stream.current = s;

          stream.current
            .getAudioTracks()
            .forEach((track) => (track.enabled = localAudioEnabled));

          stream.current
            .getVideoTracks()
            .forEach((track) => (track.enabled = !!localVideoEnabled));

          const fallbackStun = { urls: ['stun:stun.l.google.com:19302'] };

          const resolveIceServers = async () => {
            if (iceServers?.length) return [...iceServers, fallbackStun];
            if (initiator) {
              try {
                const servers: any = await Api.get('/call/ice-servers');
                if (servers?.length) return [...servers, fallbackStun];
              } catch (e) {}
            }
            return [fallbackStun];
          };

          resolveIceServers().then((servers) => {
            peerRef.current = new SimplePeer({
              config: {
                iceServers: servers,
              },
              initiator: initiator,
              trickle: false,
              stream: stream.current,
            });

          if (localVideo.current && hasVideoDevices) {
            localVideo.current.srcObject = stream.current!;
          }

          peerRef.current.on('error', (err) => console.log('error', err));

          peerRef.current.on('signal', (data) => {
            // @ts-ignore
            console.log('on signal', data);
            initiator
              ? dispatch(
                  imActions.call({
                    peerId,
                    description: btoa(JSON.stringify(data)),
                  }),
                )
              : dispatch(imActions.callSendAccept(data as RTCSessionDescription));
          });

          if (!initiator) {
            console.log('remoteDescription', remoteDescription);
            peerRef.current?.signal(remoteDescription as any);
          }

          peerRef.current.on('stream', (stream) => {
            console.log('on stream');
            if (remoteVideo.current) {
              remoteVideo.current.srcObject = stream;
            }

            statReachGoal('call_success', {
              peerId,
            });
          });

          peerRef.current.on('data', (d) => {
            const [key, rowData] = d.toString().split('\r');
            let data = {};
            if (rowData) {
              try {
                data = JSON.parse(rowData);
              } catch (e) {
                console.error(e);
              }
            }
            if (key === 'audioMute') {
              setRemoteAudioEnabled(false);
            } else if (key === 'audioUnmute') {
              setRemoteAudioEnabled(true);
            } else if (key === 'videoMute') {
              setRemoteVideoEnabled(false);
            } else if (key === 'videoUnmute') {
              setRemoteVideoEnabled(true);
            } else if (key === 'setVideoSize') {
              setRemoteScreen(data as any);
            } else if (key === 'hangup') {
              dispatch(imActions.setCallStatus(CallStatus.COMPLETED));
            }
          });

          peerRef.current.on('connect', () => {
            console.log('on connect');
            dispatch(imActions.setCallStatus(CallStatus.CONNECTED));
          });

          peerRef.current.on('close', () => {
            console.log('on close');
            dispatch(imActions.closeConnectionCall());
            stream.current?.getTracks().forEach((track) => track.stop());
            setHasStream(false);
            peerRef.current?.destroy();
          });
          });
        })
        .catch((err) => {
          console.log('err', err);

          statReachGoal('call_error', {
            peerId,
          });

          dispatch(imActions.hangup());
          dispatch(toastActions.setToastFail(t('call_permissions')));
        });
    });

    return () => {
      console.log('destroy');
      document.body.classList.remove('call');
      peerRef.current?.destroy();
      statReachGoal('call_close', {
        peerId,
      });
      if (callStartTime.current) {
        const callDurationTime = Date.now() - callStartTime.current;
        statReachGoal('call_end', {
          peerId,
          time: (callDurationTime / 1000).toFixed(),
        });
      }
    };
  }, []);

  useEffect(() => {
    if (remoteDescription && initiator) {
      peerRef.current?.signal(remoteDescription as any);
    }
  }, [remoteDescription]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const video = entries[0].contentRect;
      peerRef.current?.send(
        'setVideoSize\r' +
          JSON.stringify({
            width: video.width,
            height: video.height,
          }),
      );
    });
    remoteVideo.current && observer.observe(remoteVideo.current);
    return () => {
      remoteVideo.current && observer.unobserve(remoteVideo.current);
    };
  }, []);

  const remoteScreenRate = remoteScreen.width / remoteScreen.height;
  const localVideoSize = { width: 0, height: 0 };
  const sideMaxSize = 160;
  if (remoteScreenRate > 1) {
    localVideoSize.width = sideMaxSize;
    localVideoSize.height = sideMaxSize / remoteScreenRate;
  } else {
    localVideoSize.height = sideMaxSize;
    localVideoSize.width = sideMaxSize * remoteScreenRate;
  }

  const handelToggleUI = useCallback(() => {
    setDisplayUI(!displayUI);
  }, [displayUI]);

  const timer = useMemo(
    () =>
      (hours ? [hours, minutes, seconds] : [minutes, seconds])
        .map((n) => (n.toString().length === 1 ? '0' + n : n))
        .join(':'),
    [hours, minutes, seconds],
  );

  const displayStatus: string = useMemo(() => {
    if (status === CallStatus.RINGING) {
      return t('call_status_ringing');
    } else if (status === CallStatus.CONNECTING) {
      return t('call_status_connecting');
    } else if (status === CallStatus.CONNECTED) {
      return timer;
    }
    return '';
  }, [status, timer]);

  const handleToggleFullscreen = useCallback(
    (e) => {
      e.stopPropagation();
      handle.active ? handle.exit() : handle.enter();
    },
    [handle.active],
  );

  return (
    <ModalWrapper name="call">
      <div className="Call" onClick={handelToggleUI}>
        <FullScreen handle={handle}>
          {!remoteVideoEnabled ? (
            <div
              className="Call__placeholder"
              style={{
                backgroundImage: `url(${user?.photo})`,
              }}
            >
              <div className="Call__placeholder__content">
                <UnitPhoto
                  nftIndicator={false}
                  onlineIndicator={false}
                  size={'extra-large'}
                  unit={user}
                />
                <div className="Call__placeholder__content__name">
                  <UnitName unit={user} />
                </div>
                <div className="Call__placeholder__content__status">{displayStatus}</div>
              </div>
            </div>
          ) : (
            <div
              className={cn('Call__info', {
                hidden: !displayUI,
              })}
            >
              <div className="Call__placeholder__content__name">
                <UnitName unit={user} />
              </div>
              <div className="Call__placeholder__content__status">{displayStatus}</div>
            </div>
          )}
          {!remoteAudioEnabled && (
            <div className="Call__info__mute">
              <IcMicOff28 />
              {t(user.sex === 1 ? 'call_peer_muted_sex_1' : 'call_peer_muted_sex_0', {
                name: user.firstName,
              })}
            </div>
          )}
          <video
            className={cn('Call__localVideo', {
              active: localVideoEnabled && remoteScreen.height > 0,
            })}
            style={{
              width: localVideoSize.width,
              height: localVideoSize.height,
            }}
            ref={localVideo}
            muted
            autoPlay
            playsInline
          />
          <video
            className={cn('Call__remoteVideo', {
              active: remoteVideoEnabled,
            })}
            ref={remoteVideo}
            autoPlay
            playsInline
          />
          <div
            className={cn('Call__buttons', {
              hidden: !displayUI && remoteVideoEnabled,
            })}
          >
            {isConnected && (
              <>
                {isDesktop && (
                  <RoundButton
                    onClick={handleToggleFullscreen}
                    size="large"
                    mode={handle.active ? 'active' : undefined}
                    icon={<IcFullscreen28 />}
                  />
                )}
                {localVideoEnabled !== null && (
                  <RoundButton
                    onClick={handleToggleVideo}
                    size="large"
                    mode={localVideoEnabled ? 'active' : undefined}
                    icon={<IcCam28 />}
                  />
                )}
                <RoundButton
                  onClick={handleToggleAudio}
                  size="large"
                  mode={!localAudioEnabled ? 'active' : undefined}
                  icon={<IcMicOff28 />}
                />
              </>
            )}
            <RoundButton
              disabled={!hasStream}
              onClick={handleHangUp}
              size="large"
              icon={<IcClose28 />}
              mode="distraction"
            />
          </div>
        </FullScreen>
      </div>
    </ModalWrapper>
  );
};

export const Call = () => {
  const { peerId, type, status } = useSelector(imCallSelector);

  if (peerId && type === CallType.INCOMING && status === CallStatus.RINGING) {
    return <CallPush />;
  } else if (peerId && ![CallStatus.REJECTED, null].includes(status)) {
    return <CallComponent />;
  } else {
    return null;
  }
};
